import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Briefcase,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Save,
  X,
  Search,
} from "lucide-react";
import "./AdminJobs.css";

const EMPLOYMENT_TYPES = [
  "FULL_TIME",
  "PART_TIME",
  "CONTRACTOR",
  "TEMPORARY",
  "INTERN",
  "OTHER",
];

const STATUSES = ["draft", "active", "closed"];

const EMPTY_FORM = {
  title: "",
  industry: "",
  country: "",
  city: "",
  employment_type: "FULL_TIME",
  summary: "",
  description: "",
  requirements_text: "",
  apply_url: "",
  status: "active",
  expires_at: "",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function AdminJobs({ apiUrl, token }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/admin/jobs`, { headers: authHeaders });
      if (!res.ok) throw new Error("Failed to load jobs");
      const data = await res.json();
      setJobs(data.jobs || []);
    } catch (err) {
      toast.error(err.message || "Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }, [apiUrl, authHeaders]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (job) => {
    setEditingId(job.id);
    setForm({
      title: job.title || "",
      industry: job.industry || "",
      country: job.country || "",
      city: job.city || "",
      employment_type: job.employment_type || "FULL_TIME",
      summary: job.summary || "",
      description: job.description || "",
      requirements_text: (job.requirements || []).join("\n"),
      apply_url: job.apply_url || "",
      status: job.status || "active",
      expires_at: job.expires_at ? job.expires_at.slice(0, 10) : "",
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        industry: form.industry.trim(),
        country: form.country.trim(),
        city: form.city.trim() || null,
        employment_type: form.employment_type,
        summary: form.summary.trim(),
        description: form.description.trim(),
        requirements: form.requirements_text
          .split("\n")
          .map((r) => r.trim())
          .filter(Boolean),
        apply_url: form.apply_url.trim() || null,
        status: form.status,
        expires_at: form.expires_at
          ? new Date(form.expires_at).toISOString()
          : null,
      };
      const url = editingId
        ? `${apiUrl}/api/admin/jobs/${editingId}`
        : `${apiUrl}/api/admin/jobs`;
      const method = editingId ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to save job");
      }
      toast.success(editingId ? "Job updated" : "Job created");
      closeForm();
      fetchJobs();
    } catch (err) {
      toast.error(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (job) => {
    const next = job.status === "active" ? "draft" : "active";
    try {
      const res = await fetch(`${apiUrl}/api/admin/jobs/${job.id}`, {
        method: "PUT",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ status: next }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success(`Job ${next === "active" ? "published" : "unpublished"}`);
      fetchJobs();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const removeJob = async (job) => {
    if (!window.confirm(`Delete "${job.title}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${apiUrl}/api/admin/jobs/${job.id}`, {
        method: "DELETE",
        headers: authHeaders,
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Job deleted");
      fetchJobs();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const visible = jobs.filter((j) => {
    if (filter !== "all" && j.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !(j.title || "").toLowerCase().includes(q) &&
        !(j.industry || "").toLowerCase().includes(q) &&
        !(j.country || "").toLowerCase().includes(q)
      )
        return false;
    }
    return true;
  });

  return (
    <section className="admin-jobs">
      <header className="admin-jobs__header">
        <div className="admin-jobs__title">
          <Briefcase size={22} />
          <h2>Job Postings</h2>
          <span className="admin-jobs__count">{jobs.length}</span>
        </div>
        <div className="admin-jobs__actions">
          <div className="admin-jobs__search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search title, industry, country…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="admin-jobs__filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All ({jobs.length})</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)} (
                {jobs.filter((j) => j.status === s).length})
              </option>
            ))}
          </select>
          <button
            type="button"
            className="admin-jobs__create"
            onClick={openCreate}
          >
            <Plus size={16} /> New job
          </button>
        </div>
      </header>

      {loading ? (
        <div className="admin-jobs__empty">Loading jobs…</div>
      ) : visible.length === 0 ? (
        <div className="admin-jobs__empty">
          {jobs.length === 0
            ? "No jobs yet. Create your first posting."
            : "No jobs match your filters."}
        </div>
      ) : (
        <div className="admin-jobs__table-wrap">
          <table className="admin-jobs__table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Industry</th>
                <th>Location</th>
                <th>Type</th>
                <th>Status</th>
                <th>Posted</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {visible.map((job) => (
                <tr key={job.id}>
                  <td>
                    <div className="admin-jobs__cell-title">{job.title}</div>
                    <div className="admin-jobs__cell-sub">{job.slug}</div>
                  </td>
                  <td>{job.industry}</td>
                  <td>
                    {job.city ? `${job.city}, ` : ""}
                    {job.country}
                  </td>
                  <td>
                    <span className="admin-jobs__chip">{job.employment_type}</span>
                  </td>
                  <td>
                    <span className={`admin-jobs__status admin-jobs__status--${job.status}`}>
                      {job.status}
                    </span>
                  </td>
                  <td>{fmtDate(job.posted_at)}</td>
                  <td className="admin-jobs__row-actions">
                    <button
                      type="button"
                      className="admin-jobs__icon-btn"
                      onClick={() => toggleStatus(job)}
                      title={job.status === "active" ? "Unpublish" : "Publish"}
                    >
                      {job.status === "active" ? (
                        <EyeOff size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>
                    <button
                      type="button"
                      className="admin-jobs__icon-btn"
                      onClick={() => openEdit(job)}
                      title="Edit"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      type="button"
                      className="admin-jobs__icon-btn admin-jobs__icon-btn--danger"
                      onClick={() => removeJob(job)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div
            className="modal-content admin-jobs__modal"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="admin-jobs__modal-head">
              <h2>
                <Briefcase size={18} /> {editingId ? "Edit job" : "New job"}
              </h2>
              <button
                type="button"
                className="admin-jobs__icon-btn"
                onClick={closeForm}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </header>

            <form onSubmit={submit} className="admin-jobs__form">
              <div className="admin-jobs__form-row">
                <label>
                  Title
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    required
                    maxLength={200}
                    placeholder="e.g. Senior Site Supervisor"
                  />
                </label>
                <label>
                  Industry
                  <input
                    type="text"
                    value={form.industry}
                    onChange={(e) =>
                      setForm({ ...form, industry: e.target.value })
                    }
                    required
                    maxLength={120}
                    placeholder="e.g. Construction & Infrastructure"
                  />
                </label>
              </div>

              <div className="admin-jobs__form-row">
                <label>
                  Country
                  <input
                    type="text"
                    value={form.country}
                    onChange={(e) =>
                      setForm({ ...form, country: e.target.value })
                    }
                    required
                    maxLength={80}
                    placeholder="e.g. Saudi Arabia"
                  />
                </label>
                <label>
                  City (optional)
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    maxLength={120}
                    placeholder="e.g. Riyadh"
                  />
                </label>
              </div>

              <div className="admin-jobs__form-row">
                <label>
                  Employment type
                  <select
                    value={form.employment_type}
                    onChange={(e) =>
                      setForm({ ...form, employment_type: e.target.value })
                    }
                  >
                    {EMPLOYMENT_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t.replace("_", " ")}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Status
                  <select
                    value={form.status}
                    onChange={(e) =>
                      setForm({ ...form, status: e.target.value })
                    }
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label>
                Short summary
                <input
                  type="text"
                  value={form.summary}
                  onChange={(e) =>
                    setForm({ ...form, summary: e.target.value })
                  }
                  required
                  maxLength={500}
                  placeholder="One-line role summary for the listings page"
                />
              </label>

              <label>
                Full description
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  required
                  rows={6}
                  maxLength={8000}
                  placeholder="Day-to-day responsibilities, scope, project context…"
                />
              </label>

              <label>
                Requirements
                <textarea
                  value={form.requirements_text}
                  onChange={(e) =>
                    setForm({ ...form, requirements_text: e.target.value })
                  }
                  rows={5}
                  placeholder="One per line — e.g. Minimum 3 years experience"
                />
                <span className="admin-jobs__hint">One requirement per line</span>
              </label>

              <div className="admin-jobs__form-row">
                <label>
                  Apply URL (optional)
                  <input
                    type="url"
                    value={form.apply_url}
                    onChange={(e) =>
                      setForm({ ...form, apply_url: e.target.value })
                    }
                    maxLength={500}
                    placeholder="https:// …"
                  />
                </label>
                <label>
                  Expires on (optional)
                  <input
                    type="date"
                    value={form.expires_at}
                    onChange={(e) =>
                      setForm({ ...form, expires_at: e.target.value })
                    }
                  />
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={closeForm}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={saving}>
                  <Save size={16} /> {saving ? "Saving…" : editingId ? "Save changes" : "Create job"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
